import {
  prepareSvgWithResolvedAdUseRefs,
  serializeSvg,
} from "src/pages/ad-studio/layers/utils";
import {
  preloadFontsIntoDocument,
  removePreloadedFonts,
} from "src/pages/ad-studio/layers/utils/preload-fonts-into-document";
import { rasterizeImageToBlob } from "src/pages/ad-studio/layers/utils/rasterize-image-to-blob";
import { clampOutputScale } from "src/pages/ad-studio/layers/utils/clamp-output-scale";

export async function captureSvgAsImage(args: {
  svgString: string;
  fontRules: string[];
  componentAssets?: Record<string, string> | null;
  apiBase?: string;
  outputScale?: number;
  /** Cap on output width in px; clamps `outputScale` (never below 1). */
  maxOutputWidth?: number;
  /** Cap on total output pixels; clamps `outputScale` (never below 1). */
  maxOutputPixels?: number;
}): Promise<Blob> {
  const { svgString, fontRules } = args;

  const {
    root: svgEl,
    width,
    height,
  } = await prepareSvgWithResolvedAdUseRefs(svgString, {
    componentAssets: args.componentAssets,
    apiBase: args.apiBase,
  });
  const doc = svgEl.ownerDocument!;

  const images = svgEl.querySelectorAll("image");
  await Promise.all(
    Array.from(images).map(async (img) => {
      const href =
        img.getAttribute("href") ||
        img.getAttributeNS("http://www.w3.org/1999/xlink", "href");
      if (!href || href.startsWith("data:")) return;

      const fetchUrl =
        href.includes("/agents/files?") && !href.includes("stream=")
          ? `${href}&stream=true`
          : href;
      const includeCredentials =
        !!args.apiBase && fetchUrl.includes(args.apiBase);
      const res = await fetch(
        fetchUrl,
        includeCredentials
          ? { credentials: "include" }
          : { mode: "cors", cache: "no-store" },
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      img.setAttribute("href", dataUrl);
      img.removeAttributeNS("http://www.w3.org/1999/xlink", "href");
    }),
  );

  if (fontRules.length > 0) {
    let defs = svgEl.querySelector("defs");
    if (!defs) {
      defs = doc.createElementNS("http://www.w3.org/2000/svg", "defs");
      svgEl.insertBefore(defs, svgEl.firstChild);
    }
    const style = doc.createElementNS("http://www.w3.org/2000/svg", "style");
    style.textContent = fontRules.join("\n");
    defs.appendChild(style);
  }

  svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svgEl.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

  const svgData = serializeSvg(svgEl);
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`;
  const outputScale = clampOutputScale({
    scale: args.outputScale ?? 1,
    width,
    height,
    maxOutputWidth: args.maxOutputWidth,
    maxOutputPixels: args.maxOutputPixels,
  });
  const outputWidth = Math.round(width * outputScale);
  const outputHeight = Math.round(height * outputScale);

  // An `<img>`-loaded SVG is an isolated document with no access to the parent
  // `document.fonts`, and `img.onload` for an SVG resolves on markup parse, not
  // on font decode. Preload the same embedded `@font-face` bytes into the
  // capturing document and await their decode BEFORE `drawImage`, so the brand
  // font is rasterized instead of a system fallback. Best-effort — a failed
  // font never aborts the export.
  const preloadedFonts = await preloadFontsIntoDocument(fontRules);

  try {
    const imgEl = new Image();
    imgEl.width = outputWidth;
    imgEl.height = outputHeight;

    const imgLoaded = new Promise<void>((resolve, reject) => {
      imgEl.onload = () => resolve();
      imgEl.onerror = () => reject(new Error("Failed to load SVG as image"));
      imgEl.src = svgDataUrl;
    });
    // Prefer `decode()` (resolves once the image is fully decoded and
    // paintable), falling back to the onload promise where `decode()` is
    // unavailable.
    await (typeof imgEl.decode === "function"
      ? imgEl.decode().catch(() => imgLoaded)
      : imgLoaded);

    return await rasterizeImageToBlob({
      image: imgEl,
      width: outputWidth,
      height: outputHeight,
    });
  } finally {
    // The preloaded faces were only needed for the isolated SVG's decode above;
    // drop them so repeated captures don't leak into `document.fonts`.
    removePreloadedFonts(preloadedFonts);
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
